import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { RotateCcw } from "lucide-react";
import { MaskLines } from "./Reveal";
import { Btn } from "./Bits";

const STAGES = ["Field Data", "Survey Coordinates", "CAD Linework", "Final Drawing"];
const PTS = [[180, 150], [430, 110], [640, 190], [600, 430], [300, 470], [150, 330]];
const SHOTS = [[260, 230], [380, 260], [500, 300], [330, 360], [470, 380], [560, 260], [240, 400]];
const contour = (i) => {
  const r = 50 + i * 34;
  return `M ${420 - r} 290 C ${420 - r} ${290 - r * 0.7}, ${420 + r * 0.3} ${290 - r * 0.9}, ${420 + r} ${290 - r * 0.2} S ${420 + r * 0.4} ${290 + r * 0.85}, ${420 - r * 0.3} ${290 + r * 0.7} S ${420 - r} ${290 + r * 0.3}, ${420 - r} 290`;
};

const Plane = ({ stage }) => {
  const on = (n) => (stage >= n ? "on" : "off");
  const fade = { off: { opacity: 0 }, on: { opacity: 1 } };
  const path = { off: { pathLength: 0, opacity: 0 }, on: { pathLength: 1, opacity: 1 } };
  return (
    <svg viewBox="0 0 800 600" className="h-full w-full" fill="none" aria-hidden="true">
      <motion.g variants={fade} initial="off" animate={on(1)} transition={{ duration: 1 }} stroke="#5AA9E6" strokeOpacity=".22" strokeWidth=".6">
        {Array.from({ length: 21 }).map((_, i) => <path key={`v${i}`} d={`M${i * 40} 0V600`} />)}
        {Array.from({ length: 16 }).map((_, i) => <path key={`h${i}`} d={`M0 ${i * 40}H800`} />)}
      </motion.g>
      <g stroke="#8EC3EE" strokeWidth="1">
        {Array.from({ length: 7 }).map((_, i) => (
          <motion.path key={i} d={contour(i)} variants={path} initial="off" animate={on(2)} strokeWidth={i % 3 === 0 ? 1.5 : 0.7}
            transition={{ duration: 1.8, delay: i * 0.12, ease: "easeInOut" }} />
        ))}
      </g>
      {SHOTS.map(([x, y], i) => (
        <motion.g key={`s${i}`} initial={{ opacity: 0, scale: 0 }} animate={stage >= 1 ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0 }} transition={{ delay: 0.3 + i * 0.07 }} style={{ originX: `${x}px`, originY: `${y}px` }}>
          <path d={`M${x - 4} ${y}h8M${x} ${y - 4}v8`} stroke="#D8B66A" />
          <text x={x + 6} y={y - 5} fontSize="9" fill="#D8B66A" fontFamily="JetBrains Mono">{(98.2 + i * 0.73).toFixed(2)}</text>
        </motion.g>
      ))}
      <motion.path d={`M${PTS.map((p) => p.join(" ")).join(" L")} Z`} stroke="#FFFFFF" strokeWidth="2.2" variants={path} initial="off" animate={on(3)} transition={{ duration: 2, ease: "easeInOut" }} />
      <motion.g variants={fade} initial="off" animate={on(3)} transition={{ delay: 1 }} fill="#E6EEF5" fontFamily="JetBrains Mono" fontSize="11">
        <text x="250" y="118" transform="rotate(-9 250 118)">N 80°54'12" E 252.07'</text>
        <text x="540" y="135" transform="rotate(21 540 135)">S 69°08'40" E 224.61'</text>
        <text x="630" y="320" transform="rotate(95 630 320)">S 9°44'03" W 243.38'</text>
        <text x="380" y="472" transform="rotate(8 380 472)">N 82°24'E 303.6'</text>
      </motion.g>
      {PTS.map(([x, y], i) => (
        <motion.g key={i} initial={{ opacity: 0, scale: 0 }} animate={stage >= 1 ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0 }} transition={{ delay: i * 0.1, type: "spring", stiffness: 260, damping: 18 }} style={{ originX: `${x}px`, originY: `${y}px` }}>
          <circle cx={x} cy={y} r="6" stroke="#FFFFFF" strokeWidth="1.4" />
          <path d={`M${x - 14} ${y}h28M${x} ${y - 14}v28`} stroke="#FFFFFF" strokeWidth=".7" />
          <text x={x + 10} y={y + 18} fontSize="9.5" fill="#BFD7EC" fontFamily="JetBrains Mono">{`P${101 + i} N${(5021.3 + i * 37.4).toFixed(1)} E${(2210.8 + i * 21.9).toFixed(1)}`}</text>
        </motion.g>
      ))}
      <motion.g variants={fade} initial="off" animate={on(4)} transition={{ duration: 0.9 }} stroke="#FFFFFF">
        <rect x="12" y="12" width="776" height="576" strokeWidth="1.2" />
        <rect x="20" y="20" width="760" height="560" strokeWidth=".5" />
        <rect x="560" y="500" width="220" height="80" strokeWidth=".8" fill="#0A1D30" fillOpacity=".7" />
        <path d="M560 528H780M560 554H780M680 528V580" strokeWidth=".5" />
        <text x="572" y="519" fontSize="11" fill="#FFFFFF" stroke="none" fontFamily="Manrope" fontWeight="800" letterSpacing="2">GEO DRAFTING</text>
        <text x="572" y="545" fontSize="8.5" fill="#BFD7EC" stroke="none" fontFamily="JetBrains Mono">BOUNDARY &amp; TOPO PLAN</text>
        <text x="572" y="571" fontSize="8.5" fill="#D8B66A" stroke="none" fontFamily="JetBrains Mono">ILLUSTRATIVE</text>
        <text x="690" y="571" fontSize="8.5" fill="#BFD7EC" stroke="none" fontFamily="JetBrains Mono">SHT 1/1</text>
        <path d="M60 520v-40M52 492l8-12 8 12" strokeWidth="1.2" /><text x="55" y="536" fontSize="10" fill="#FFFFFF" stroke="none" fontFamily="JetBrains Mono">N</text>
        <path d="M100 540h120M100 535v10M160 537v6M220 535v10" strokeWidth="1" />
        <rect x="320" y="250" width="90" height="60" strokeWidth="1" fill="#1769AA" fillOpacity=".25" />
        <text x="332" y="285" fontSize="9" fill="#FFFFFF" stroke="none" fontFamily="JetBrains Mono">FF 101.25</text>
      </motion.g>
    </svg>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const [stage, setStage] = useState(reduce ? 4 : 0);
  const [run, setRun] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const tilt = useTransform(scrollYProgress, [0, 1], [0, 22]);
  const planeY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  useEffect(() => {
    if (reduce) { setStage(4); return undefined; }
    setStage(0);
    const ts = [300, 2000, 3800, 6000].map((ms, i) => setTimeout(() => setStage(i + 1), ms));
    return () => ts.forEach(clearTimeout);
  }, [run, reduce]);

  return (
    <section ref={ref} data-testid="home-hero" className="on-dark relative min-h-[100svh] overflow-hidden bg-[#0A1D30] text-white">
      <motion.div style={{ y: imgY }} className="absolute inset-0">
        <motion.img src="/img/aerial.jpg" alt="Bird's-eye aerial view of farmland parcels, a road junction and a pond" fetchPriority="high"
          className="h-[118%] w-full object-cover" style={{ filter: "saturate(.55) contrast(1.05)" }}
          initial={{ opacity: 0, scale: 1.12 }} animate={{ opacity: stage >= 4 ? 0.22 : 0.6, scale: 1 }} transition={{ duration: 2.2, ease: [0.2, 0.7, 0.2, 1] }} />
      </motion.div>
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(10,29,48,.7)_0%,rgba(10,29,48,.55)_45%,#0A1D30_100%)] lg:bg-[linear-gradient(90deg,#0A1D30_0%,rgba(10,29,48,.88)_38%,rgba(10,29,48,.25)_75%,rgba(10,29,48,.5)_100%)]" />
      <div className="absolute inset-0 grain" />

      <motion.div style={{ rotateX: tilt, y: planeY, transformPerspective: 1400 }}
        className="absolute right-[-12%] top-[18%] w-[130%] opacity-45 sm:opacity-60 lg:opacity-100 lg:right-[-2%] lg:top-[12%] lg:w-[60%] aspect-[4/3]">
        <motion.div className="h-full w-full" initial={{ rotateX: 40, rotateZ: -6, scale: 1.05 }}
          animate={stage >= 4 ? { rotateX: 0, rotateZ: 0, scale: 1 } : { rotateX: 40, rotateZ: -6, scale: 1.05 }}
          transition={{ duration: 1.6, ease: [0.7, 0, 0.2, 1] }} style={{ transformPerspective: 1400 }}>
          <Plane stage={stage} />
        </motion.div>
      </motion.div>

      <div className="relative z-10 mx-auto flex min-h-[100svh] max-w-[1400px] flex-col justify-end px-5 sm:px-8 pt-[120px] pb-36 lg:justify-center lg:pb-40">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} data-testid="hero-technical-label"
          className="eyebrow text-[#D8B66A] text-[11px] sm:text-[12px]">Land Surveying &amp; Mapping Solution | CAD Drafting | Survey Data Processing</motion.p>
        <h1 className="mt-6 max-w-3xl font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight" data-testid="hero-headline">
          <MaskLines delay={0.25} lines={["Land Surveying", "CAD Drafting.", ["Precision in", "text-[#5AA9E6]"], ["Every Line.", "text-[#5AA9E6]"]]} />
        </h1>
        <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-6 font-display text-lg md:text-xl font-semibold text-[#D8B66A]">From Coordinates to CAD Excellence.</motion.p>
        <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.7 }}
          className="mt-4 max-w-xl text-sm md:text-base leading-relaxed text-[#d5dee8]">
          Professional survey CAD drafting and data processing for surveying companies and technical professionals. We transform supplied field data into organized digital drawings tailored to your project requirements.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.7 }}
          className="mt-9 flex flex-wrap items-center gap-4">
          <Btn to="/get-started" testid="hero-get-started-button">Get Started</Btn>
          <Btn to="/services" variant="ghost" testid="hero-explore-services-button">Explore All Survey Types</Btn>
          <Link to="/pricing#quote" data-testid="hero-request-quote-link" className="u-link text-[12px] font-bold uppercase tracking-[0.16em] text-white ml-1">Request a Quote</Link>
        </motion.div>
      </div>

      <div className="absolute bottom-0 inset-x-0 z-10 border-t border-white/10 bg-[#0A1D30]/70 backdrop-blur-md">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 flex items-center gap-4 h-16">
          <ol className="flex-1 grid grid-cols-4 gap-2 sm:gap-4" data-testid="hero-stage-indicator">
            {STAGES.map((s, i) => (
              <li key={s} className="relative">
                <div className="h-px w-full bg-white/15"><motion.div className="h-px bg-[#D8B66A] origin-left" animate={{ scaleX: stage > i ? 1 : 0 }} transition={{ duration: 0.8 }} /></div>
                <p className={`mt-2 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.14em] transition-colors duration-500 ${stage > i ? "text-white" : "text-[#7f97ad]"}`}>
                  <span className="text-[#D8B66A]">0{i + 1}</span><span className="hidden sm:inline"> {s}</span>
                </p>
              </li>
            ))}
          </ol>
          <button type="button" onClick={() => setRun((r) => r + 1)} data-testid="hero-replay-button" aria-label="Replay hero animation"
            className="h-9 w-9 grid place-items-center border border-white/20 text-white hover:border-[#5AA9E6] hover:text-[#5AA9E6] transition-colors">
            <RotateCcw className="h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
};
